import { Box, LinearProgress, List, ListItem, ListItemAvatar, ListItemText, Tab, Tabs, Typography } from '@mui/material'
import { dayjs } from '@tunasong/models'
import type { GameResult, Persisted, UserStats } from '@tunasong/schemas'
import { UserAvatar } from '@tunasong/ui-lib'
import { useState, type FC } from 'react'
import type { Game } from '@tunasong/schemas'
import { Table, TableBody, TableRow, TableCell } from '@mui/material'
export interface ScorePanelProps {
  game: Game
  leaderBoard?: Persisted<GameResult>[]
  score: number
  userStats?: UserStats
  bonusTime?: number
  maxBonusTime?: number
}

export const ScorePanel: FC<ScorePanelProps> = props => {
  const { score = 0, userStats, bonusTime = 0, maxBonusTime = 10, leaderBoard } = props
  const { xp, scoreHigh } = userStats ?? { xp: 0, scoreSum: 0 }
  const [tab, setTab] = useState(0)

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Box sx={{ display: 'flex', minHeight: 200 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            orientation="vertical"
            aria-label="score panel tabs"
            sx={{ borderRight: 1, borderColor: 'divider', minWidth: 140 }}
          >
            <Tab label="Score" />
            <Tab label="User Stats" />
            <Tab label="Leaderboard" />
          </Tabs>
          <Box sx={{ flex: 1, pl: 3 }}>
            {tab === 0 && (
              <>
                <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="h2">{score}</Typography>
                  <Typography variant="h5">Score</Typography>
                </Box>
                {bonusTime > 0 ? (
                  <>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Bonus Time Remaining
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(bonusTime / maxBonusTime) * 100}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
                    >
                      {Math.ceil(bonusTime)}s
                    </Typography>
                  </>
                ) : null}
              </>
            )}
            {tab === 1 && (
              <Box sx={{ pt: 2 }}>
                {userStats ? (
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Streak</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{userStats.streak}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Level</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {userStats.level.name} ({userStats.level.description})
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Proficiency</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{userStats.level.proficiency}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Next level</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{userStats.level.maxScore + 1 - xp} XP remaining</Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Your high score</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{scoreHigh}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">XP</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{xp}</Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No user stats available.
                  </Typography>
                )}
              </Box>
            )}
            {tab === 2 && (
              <Box sx={{ pt: 2 }}>
                {leaderBoard && leaderBoard.length > 0 ? (
                  <List>
                    {leaderBoard.map((entry, idx) => (
                      <ListItem key={entry.userId} secondaryAction={entry.score}>
                        <ListItemAvatar>
                          <UserAvatar userId={entry.userId} size="small" />
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${idx + 1}. ${entry.nickName}`}
                          secondary={dayjs(entry.createdAt).format('MMM D, YYYY')}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No leaderboard data.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default ScorePanel
